import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

export interface ParameterData {
  label: string;
  value: number | string;
  unit?: string;
  ci?: [number, number];
  interpretation?: string;
  description?: string;
  color?: string;
  skipInterpretation?: boolean; // Add flag to skip auto-interpretation
}

interface ParameterGroupCardProps {
  title: string;
  parameters: ParameterData[];
  variant?: 'weibull' | 'life' | 'fit';
  className?: string;
  compact?: boolean;
  helpText?: string; // Add help text for info icon
}

/**
 * Grouped parameter card for displaying related Weibull analysis parameters
 */
export const ParameterGroupCard: React.FC<ParameterGroupCardProps> = ({
  title,
  parameters,
  variant = 'weibull',
  className = '',
  compact = false,
  helpText
}) => {
  // Determine card styling based on variant
  const variantStyles = {
    weibull: 'border-blue-200 bg-blue-50',
    life: 'border-green-200 bg-green-50',
    fit: 'border-purple-200 bg-purple-50'
  };

  // Helper function to interpret shape parameter
  const getShapeInterpretation = (value: number): { text: string; color: string } => {
    if (value < 1) return { text: 'Infant mortality', color: 'text-red-600' };
    if (value >= 1 && value < 1.5) return { text: 'Random failures', color: 'text-yellow-600' };
    if (value >= 1.5 && value < 4) return { text: 'Early wear-out', color: 'text-blue-600' };
    return { text: 'Rapid wear-out', color: 'text-purple-600' };
  };

  // Helper function to interpret R² value
  const getFitQuality = (value: number): { text: string; color: string } => {
    if (value >= 0.95) return { text: 'Excellent fit', color: 'text-green-600' };
    if (value >= 0.90) return { text: 'Good fit', color: 'text-blue-600' };
    if (value >= 0.80) return { text: 'Fair fit', color: 'text-yellow-600' };
    return { text: 'Poor fit', color: 'text-red-600' };
  };

  return (
    <div
      className={`
        flex-1 min-w-[300px]
        rounded-lg border shadow-sm
        ${variantStyles[variant]}
        ${className}
      `}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1">
            {title}
            {helpText && (
              <span className="group relative">
                <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 w-64 p-2 bg-white text-gray-900 text-xs rounded-lg shadow-lg border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  <div className="whitespace-pre-line">{helpText}</div>
                  <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white" />
                </div>
              </span>
            )}
          </h3>
        </div>
      </div>

      {/* Parameters */}
      <div className={`${compact ? 'px-3 py-2 space-y-2' : 'px-4 py-3 space-y-4'}`}>
        {parameters.map((param, index) => {
          // Auto-interpret certain parameters only if not explicitly skipped
          let interpretation = param.interpretation;
          let interpretationColor = 'text-gray-600';

          if (!param.skipInterpretation) {
            // Special handling for shape parameter (only for the main parameter, not SE)
            if (param.label.toLowerCase().includes('shape') && 
                !param.label.toLowerCase().includes('se') &&
                typeof param.value === 'number') {
              const shapeInfo = getShapeInterpretation(param.value);
              interpretation = interpretation || shapeInfo.text;
              interpretationColor = shapeInfo.color;
            }

            // Special handling for R²
            if (param.label.toLowerCase().includes('r²') && typeof param.value === 'number') {
              const fitInfo = getFitQuality(param.value);
              interpretation = interpretation || fitInfo.text;
              interpretationColor = fitInfo.color;
            }
          }

          return (
            <div key={index} className="space-y-0.5">
              {/* Label and Value */}
              <div className="flex items-baseline justify-between">
                <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
                  {param.label}
                  {param.description && !param.skipInterpretation && (
                    <span className="group relative">
                      <InformationCircleIcon className="h-3 w-3 text-gray-400 cursor-help" />
                      <div className="absolute left-0 bottom-full mb-1 px-2 py-1 bg-white text-gray-900 text-xs rounded shadow-lg border border-gray-200 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                        {param.description}
                        <div className="absolute left-2 top-full w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-white" />
                      </div>
                    </span>
                  )}
                </span>
                <span className={`font-semibold ${compact ? 'text-sm' : 'text-base'} text-gray-900`}>
                  {typeof param.value === 'number' ? param.value.toFixed(3) : param.value}
                  {param.unit && <span className="text-xs text-gray-500 ml-1">{param.unit}</span>}
                </span>
              </div>

              {/* Confidence Interval - show inline if provided */}
              {param.ci && (
                <div className="text-xs text-gray-500">
                  95% CI: [{param.ci[0].toFixed(3)}, {param.ci[1].toFixed(3)}]
                </div>
              )}

              {/* Description (only shown if not using tooltip) */}
              {param.description && param.skipInterpretation && (
                <div className="text-xs text-gray-500">
                  {param.description}
                </div>
              )}

              {/* Interpretation */}
              {interpretation && (
                <div className={`text-xs ${interpretationColor} font-medium`}>
                  {interpretation}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ParameterGroupCard;
